/**
 * Pure helpers for the staff form: initial values, validation and the
 * POST /staff/invite + PUT /staff payloads. No React here so every rule can be
 * unit-tested directly.
 *
 * Several rules below exist because of production incidents — read the comment
 * on each before "simplifying" it. They were ported verbatim from the former
 * EmployeeForm.js.
 */
import {
  convertTo12HourFormat,
  convertTo24HourFormat,
} from "../../../utils/timeUtils";
import type {
  DayHours,
  DayKey,
  StaffFormInitialData,
  StaffFormIssue,
  StaffFormValues,
  StaffRole,
  StaffServiceAssignments,
  WeeklyHours,
} from "../../../types/staffForm";

export const DAYS: { key: DayKey; label: string }[] = [
  { key: "sunday", label: "Sunday" },
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A loose record (businessInfo / staff payloads are untyped JS objects). */
type LooseRecord = Record<string, any> | null | undefined;

// ── Hours ──────────────────────────────────────────────────────

const to24h = (value: unknown): string | null =>
  (convertTo24HourFormat(value as string) as string | null | undefined) ?? null;

/** Weekly hours from a staff record's `${day}_start_time` / `_end_time` columns. */
export function weeklyHoursFromStaff(staff: LooseRecord): WeeklyHours {
  return weeklyHoursFrom(staff, "start_time");
}

/** Weekly hours seeded from the salon's `${day}_open_time` / `_end_time`. */
export function weeklyHoursFromBusiness(business: LooseRecord): WeeklyHours {
  return weeklyHoursFrom(business, "open_time");
}

function weeklyHoursFrom(
  source: LooseRecord,
  startSuffix: "start_time" | "open_time",
): WeeklyHours {
  return DAYS.reduce((hours, { key }) => {
    hours[key] = {
      start: to24h(source?.[`${key}_${startSuffix}`]),
      end: to24h(source?.[`${key}_end_time`]),
    };
    return hours;
  }, {} as WeeklyHours);
}

/** A day counts as "checked" once either side is non-null ("" = blank but checked). */
export const isDayEnabled = (day: DayHours): boolean =>
  day.start != null || day.end != null;

const hasFullDay = (hours: WeeklyHours): boolean =>
  DAYS.some(({ key }) => !!hours[key].start && !!hours[key].end);

/** True when the record carried any hour column at all (vs. a payload that omits them). */
export const hoursWereLoaded = (initialData: LooseRecord): boolean =>
  DAYS.some(
    ({ key }) =>
      initialData?.[`${key}_start_time`] != null ||
      initialData?.[`${key}_end_time`] != null,
  );

/**
 * Validate only days the user actually touched this session (checked or
 * edited a time). A create must NEVER be blocked by hours auto-seeded from
 * the salon's business hours — which the operator never touched and which can
 * themselves be half-filled — so untouched days are skipped. For a touched
 * day: it must carry BOTH times (an enabled-but-blank day would send "" for
 * the missing side; null stays reserved for the deliberate "not working"
 * state) and the end must be after the start (a same-day ordering check —
 * times are zero-padded 24h "HH:MM" strings, so a lexical compare is
 * chronological; not overnight support, which the booking engine lacks).
 */
export function firstWorkingDayError(
  hours: WeeklyHours,
  touchedDays: ReadonlySet<DayKey>,
): string | null {
  for (const { key, label } of DAYS) {
    if (!touchedDays.has(key)) continue;
    const { start, end } = hours[key];
    if (!isDayEnabled(hours[key])) continue;
    if (!start || !end) {
      return `Set a start and end time for ${label}, or uncheck it.`;
    }
    if (start >= end) {
      return `${label}'s end time must be after its start time.`;
    }
  }
  return null;
}

export const BOOKABLE_NEEDS_HOURS_MESSAGE =
  "Set at least one working day before enabling a bookable calendar.";

/**
 * The working-hours check fires only when crossing into "bookable" (create,
 * explicit OFF -> ON, or a save where an absent/null flag fails safe into
 * "transition") — never on a routine edit of a known-bookable staff member.
 * Exception: flag unknown AND initialData omitted the hour columns. There the
 * nulls mean "not loaded", not "no hours" — blocking on them locked every edit
 * of such records, and the only way past overwrote a schedule the form never
 * saw. The server, which can see the real hours, enforces the precondition
 * for that case.
 */
export function bookableCalendarPreconditionError({
  initialData,
  wantsBookable,
  hours,
}: {
  initialData: LooseRecord;
  wantsBookable: boolean;
  hours: WeeklyHours;
}): string | null {
  const isCreate = !initialData?.id;
  // Strict read: absent/null is UNKNOWN and must fail safe into "transition".
  const wasKnownBookable = initialData?.has_bookable_calendar === true;
  const isOffToOnTransition = !isCreate && !wasKnownBookable && wantsBookable;
  const switchWasExplicitlyOff = initialData?.has_bookable_calendar === false;
  if (
    wantsBookable &&
    (isCreate || isOffToOnTransition) &&
    (isCreate || switchWasExplicitlyOff || hoursWereLoaded(initialData)) &&
    !hasFullDay(hours)
  ) {
    return BOOKABLE_NEEDS_HOURS_MESSAGE;
  }
  return null;
}

/**
 * Edit-mode TRUE -> FALSE asks for confirmation. This uses the LOOSE read
 * (legacy rows with no stored value render ON) because the question is about
 * the state the user saw.
 */
export const needsDisableConfirm = ({
  initialData,
  wantsBookable,
}: {
  initialData: LooseRecord;
  wantsBookable: boolean;
}): boolean =>
  !!initialData?.id &&
  initialData?.has_bookable_calendar !== false &&
  !wantsBookable;

/** Every hour key, 12h-formatted — the create (invite) shape. */
export function buildInviteSchedule(hours: WeeklyHours): Record<string, any> {
  return DAYS.reduce(
    (acc, { key }) => ({
      ...acc,
      [`${key}_start_time`]: convertTo12HourFormat(hours[key].start),
      [`${key}_end_time`]: convertTo12HourFormat(hours[key].end),
    }),
    {} as Record<string, any>,
  );
}

/**
 * The staff PUT writes every field present in the body (explicit null
 * deletes), and some staff-list payloads omit the hour columns — blanket
 * sending all 14 keys wiped real schedules in production. A day is sent only
 * if it was loaded from initialData (a clear is then an intentional null) or
 * the user set it here to a real value; a null for a never-loaded day means
 * "unknown", not "cleared", and is omitted.
 */
export function buildUpdateSchedule(
  hours: WeeklyHours,
  initialData: LooseRecord,
  touchedDays: ReadonlySet<DayKey>,
): Record<string, any> {
  return DAYS.reduce(
    (acc, { key }) => {
      const startTime = convertTo12HourFormat(hours[key].start);
      const endTime = convertTo12HourFormat(hours[key].end);
      const wasLoaded =
        initialData?.[`${key}_start_time`] != null ||
        initialData?.[`${key}_end_time`] != null;
      const setHere = touchedDays.has(key) && !!(startTime || endTime);
      if (!wasLoaded && !setHere) {
        return acc;
      }
      return {
        ...acc,
        [`${key}_start_time`]: startTime,
        [`${key}_end_time`]: endTime,
      };
    },
    {} as Record<string, any>,
  );
}

// ── General info ───────────────────────────────────────────────

export const formatPhoneForDisplay = (
  areaCode?: string | null,
  phoneNumber?: string | null,
): string =>
  phoneNumber
    ? `(${areaCode}) ${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`
    : "";

export function splitPhoneNumber(fullPhoneNumber: string): {
  digits: string;
  areaCode: string;
  phoneNumber: string;
} {
  const digits = (fullPhoneNumber || "").replace(/\D/g, "");
  return { digits, areaCode: digits.slice(0, 3), phoneNumber: digits.slice(3) };
}

export interface RoleOption {
  value: StaffRole;
  label: string;
  disabled?: boolean;
}

/**
 * Roles the current user may grant: Owners grant every role, Managers grant
 * Manager/Assistant/Employee, everyone else only Employee. A staff member whose
 * current role the user can't grant keeps it visible (disabled) instead of the
 * Select rendering blank.
 */
export function buildRoleOptions(
  currentUserRole?: string | null,
  staffRole?: StaffRole | null,
): RoleOption[] {
  const options: RoleOption[] = [];
  if (currentUserRole === "Owner") {
    options.push({ value: "Owner", label: "Owner" });
  }
  if (currentUserRole === "Manager" || currentUserRole === "Owner") {
    options.push({ value: "Manager", label: "Manager" });
    options.push({ value: "Assistant", label: "Assistant" });
  }
  options.push({ value: "Employee", label: "Employee" });
  if (staffRole && !options.some((option) => option.value === staffRole)) {
    options.unshift({ value: staffRole, label: staffRole, disabled: true });
  }
  return options;
}

/** New staff default to bookable, except Assistants. */
export const defaultBookableForRole = (role: StaffRole): boolean =>
  role !== "Assistant";

/** Field-level checks for the General Info section. */
export function validateGeneralInfo(
  values: StaffFormValues,
  { isCreate }: { isCreate: boolean },
): StaffFormIssue[] {
  const issues: StaffFormIssue[] = [];
  const required: [keyof StaffFormValues, string][] = [
    ["firstName", "First name is required"],
    ["lastName", "Last name is required"],
    ["title", "Title is required"],
    ["role", "Role is required"],
    ["email", "Email is required"],
  ];
  required.forEach(([field, message]) => {
    if (!String(values[field] ?? "").trim()) {
      issues.push({ section: "general", field, message });
    }
  });
  // Format-check only on create: an existing (locked) address must never block
  // an unrelated edit.
  if (isCreate && values.email.trim() && !EMAIL_PATTERN.test(values.email)) {
    issues.push({
      section: "general",
      field: "email",
      message: "Enter a valid email address",
    });
  }
  return issues;
}

// ── Initial values ─────────────────────────────────────────────

export function buildInitialValues({
  initialData,
  businessInfo,
  serviceAssignments,
}: {
  initialData?: StaffFormInitialData | null;
  businessInfo: LooseRecord;
  serviceAssignments: StaffServiceAssignments;
}): StaffFormValues {
  const role: StaffRole = initialData?.role || "Employee";
  return {
    firstName: initialData?.first_name || "",
    lastName: initialData?.last_name || "",
    email: initialData?.email || "",
    phone: formatPhoneForDisplay(
      initialData?.area_code,
      initialData?.phone_number,
    ),
    title: initialData?.title || "",
    role,
    status: initialData?.status || "Active",
    // Existing staff: loose read (legacy rows default ON). New staff: by role.
    hasBookableCalendar: initialData
      ? initialData.has_bookable_calendar !== false
      : defaultBookableForRole(role),
    hours: initialData
      ? weeklyHoursFromStaff(initialData)
      : weeklyHoursFromBusiness(businessInfo),
    serviceAssignments,
  };
}

// ── Payloads ───────────────────────────────────────────────────

/** POST /staff/invite body. `salon_id` is what the invite lambda reads. */
export function buildInvitePayload(
  values: StaffFormValues,
  { businessId, photoUrl }: { businessId: string; photoUrl?: string | null },
): Record<string, any> {
  return {
    first_name: values.firstName,
    last_name: values.lastName,
    email: values.email,
    phone_number: splitPhoneNumber(values.phone).digits,
    title: values.title,
    role: values.role,
    status: values.status || "Active",
    has_bookable_calendar: values.hasBookableCalendar,
    salon_id: businessId,
    ...buildInviteSchedule(values.hours),
    // The backend supports the photo on invite.
    ...(photoUrl ? { staff_photo_s3_uri: photoUrl } : {}),
  };
}

/** PUT /staff body. Email notification opt-in is owned by Settings → Notifications. */
export function buildUpdatePayload(
  values: StaffFormValues,
  {
    staffId,
    initialData,
    businessId,
    touchedDays,
    userToggledBookable,
  }: {
    staffId: string;
    initialData: LooseRecord;
    businessId: string;
    touchedDays: ReadonlySet<DayKey>;
    userToggledBookable: boolean;
  },
): Record<string, any> {
  const { areaCode, phoneNumber } = splitPhoneNumber(values.phone);
  return {
    id: staffId,
    first_name: values.firstName,
    last_name: values.lastName,
    email: values.email,
    area_code: areaCode,
    phone_number: phoneNumber,
    title: values.title,
    role: values.role,
    status: values.status,
    // Sent only if loaded or toggled here. An unknown flag rendered as the ON
    // default must not be sent as true: the server reads that as a
    // FALSE -> TRUE enable and 400s its readiness checks on a save that never
    // touched bookability. Absent means "leave it alone".
    ...((initialData?.has_bookable_calendar != null || userToggledBookable) && {
      has_bookable_calendar: values.hasBookableCalendar,
    }),
    // The location this form is showing. PUT /staff writes the per-location
    // fields (role, title, status, calendar, hours) to THIS location's
    // assignment row. Without it the backend falls back to the session's
    // last-used location, which a second tab makes wrong. Scope only: the
    // backend refuses to write this column, so it can never re-parent the
    // staff member.
    business_location_id: businessId,
    // Without a bookable calendar the hours are hidden (and so unedited):
    // leave the stored schedule alone. The backend keeps it across the toggle.
    ...(values.hasBookableCalendar &&
      buildUpdateSchedule(values.hours, initialData, touchedDays)),
  };
}

/**
 * What the record looks like right after an invite, so a retried save in the
 * same session (staff created, services failed) can PUT with the right
 * "loaded" semantics: every hour key and the calendar flag were sent.
 */
export function initialDataFromInvite(
  values: StaffFormValues,
  staffId: string,
): StaffFormInitialData {
  return {
    id: staffId,
    has_bookable_calendar: values.hasBookableCalendar,
    ...buildInviteSchedule(values.hours),
  };
}

/**
 * The server's own 4xx reason (e.g. a bookable-calendar readiness check),
 * without the "(Status Code: N)" suffix fetchWithAuth appends; a generic
 * fallback for network/5xx failures.
 */
export function getServerErrorMessage(
  error: any,
  fallback = "Could not save changes. Please try again.",
): string {
  return error?.status >= 400 &&
    error?.status < 500 &&
    error?.message &&
    error.message !== "Failed to fetch data"
    ? error.message.replace(/\s*\(Status Code: \d+\)\s*$/, "")
    : fallback;
}

/** Collects the issues for a set of sections, in the order they should surface. */
export function validateStaffForm(
  values: StaffFormValues,
  {
    initialData,
    touchedDays,
  }: {
    initialData: LooseRecord;
    touchedDays: ReadonlySet<DayKey>;
  },
): StaffFormIssue[] {
  const issues = validateGeneralInfo(values, { isCreate: !initialData?.id });
  // Per-day errors before the aggregate bookable-calendar check, so a specific
  // "Set a start and end time for Saturday" wins over the generic "set at least
  // one working day" when the only checked day is still blank.
  // Hidden hours (no bookable calendar) can't be fixed, so they don't block.
  const dayError = values.hasBookableCalendar
    ? firstWorkingDayError(values.hours, touchedDays)
    : null;
  const bookableError = dayError
    ? null
    : bookableCalendarPreconditionError({
        initialData,
        wantsBookable: values.hasBookableCalendar,
        hours: values.hours,
      });
  const hoursMessage = dayError ?? bookableError;
  if (hoursMessage) {
    issues.push({ section: "hours", message: hoursMessage });
  }
  return issues;
}

/**
 * Merge a saved staff member into a list: update the matching id in place, or
 * append a new one. Hosts use it for both the invite and the edit result.
 */
export function upsertStaffMember<T extends { id?: string }>(
  list: T[] | null | undefined,
  saved: T,
): T[] {
  const current = list ?? [];
  const exists = !!saved.id && current.some((member) => member.id === saved.id);
  return exists
    ? current.map((member) =>
        member.id === saved.id ? { ...member, ...saved } : member,
      )
    : [...current, saved];
}
